import HistoryIcon from "@mui/icons-material/History";
import RestoreIcon from "@mui/icons-material/Restore";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useRef, type MouseEvent } from "react";
import { useSelector } from "react-redux";
import Guid from "_shared/guids/guid";
import { useSession } from "users/__session/session-context";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import {
  formatProposalChangeLine,
  proposalChangeActorLabel,
  proposalChangeValueText,
  proposalCreationBaselineLabel,
  proposalEmptyValueText,
} from "work/proposals/negotiation/proposal-change-description";
import { proposalStrictJsonValuesEqual } from "work/proposals/negotiation/proposal-collection-codec";
import { describeProposalTimelineChange } from "work/proposals/negotiation/proposal-change-descriptors";
import { useGetProposalTimelineQuery } from "work/proposals/negotiation/proposal-collaboration-api";
import type {
  ProposalActorRole,
  ProposalContentOperation,
  ProposalNegotiationParty,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  buildProposalCollectionItemAspectRevertOperation,
  buildProposalCollectionItemRevertOperation,
  buildProposalScalarFieldRevertOperation,
  proposalCollectionItemAspectLabel,
  proposalCollectionItemUnreadableValueText,
  proposalCollectionItemConflictKeyAspect,
  proposalCollectionPathFromConflictKey,
  readProposalCollectionItemAspectValue,
  type ProposalCollectionItemAspect,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import { useMarkProposalChangesSeenMutation } from "work/proposals/negotiation/proposal-collaboration-api";
import {
  ProposalFieldChangeBadgeBubbles,
  useProposalFieldChangeBadgeState,
} from "work/proposals/negotiation/proposal-field-change-badge";
import { proposalFeeScheduleTeamMemberUserIds } from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import {
  proposalTimelineItemPathForField,
  proposalTimelinePathForField,
} from "work/proposals/negotiation/proposal-field-timeline-path";
import { useProposalUserNameResolver } from "work/proposals/_views/_shared/use-proposal-change-description-context";
import { useProposalFieldHistoryPopoverTrigger } from "work/proposals/negotiation/use-proposal-field-history-popover-trigger";
import {
  buildProposalTimelineView,
  proposalTimelineEntryIsContentChange,
  proposalTimelineEntryIsCreationBaseline,
  proposalTimelineTeamChange,
  type ProposalTimelineEntryView,
} from "work/proposals/negotiation/proposal-timeline";
import ProposalField, {
  scalarWithIdTimelineIdentity,
  type ProposalFieldTimelineIdentity,
  type ProposalStructuredScalarPath,
} from "work/proposals/proposal-fields/proposal-field";

/** Most recent field changes offered for revert; older history stays in the drawer's Changes tab. */
const MAX_REVERT_ENTRIES = 8;

/**
 * Whether a timeline entry is about the collection item this menu is scoped to.
 *
 * Compared case-insensitively, as the timeline panel and the label and value indexes already do: the
 * stream and the projection have not always agreed on the casing of an item id. This menu compared
 * them exactly, which was survivable only because it also read the collection's whole thread — a
 * mismatch cost a revert candidate. Now that the item is asked for on the wire it would cost the whole
 * menu, so the two comparisons have to agree.
 */
function entryBelongsToItem(entry: { itemId?: string | null }, itemId: string): boolean {
  return entry.itemId?.toLowerCase() === itemId.toLowerCase();
}

/**
 * The section holding the item's own arrivals and departures, once the columns above it have their own.
 * #4847 keeps whole-item history precisely so adding and removing a category stays reachable.
 */
const wholeItemSectionLabel = "Whole entry";

// Positions the change-badge bubbles over the icon's top-right corner rather than over the field, so
// the count reads as belonging to the control that clears it.
const HistoryIconContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  position: "relative",
}));

/**
 * What the caller receives when the user picks a previous value to revert to.
 *
 * A plain-text scalar-field revert carries `nextValue`, which the caller feeds back through the
 * field's own builder setter (see draft-proposal-details-tab.tsx's `applyScalarRevert`) so it lands as a
 * `FieldSet`/resolution op. It is the stored value, not `nextDisplayValue`'s rendering of it: for a date
 * those differ ("2026-09-28T…Z" against "Sep 28, 2026"), and writing back the rendering produced an
 * Invalid Date that failed proposal validation on every date revert. A collection-item revert (the field was built with an id, e.g.
 * `ProposalField.FeeScheduleCategory(id)`) instead carries `operation` — an already-built
 * `CollectionItemUpdated`/`CollectionItemAdded` whose `value` the caller extracts (via
 * `proposalCollectionItemRevertValue`) and re-applies through its own normal edit path; a collection
 * item has no whole-field setter to route a plain value through, and never a raw `FieldSet` (see
 * proposal-collection-item-revert.ts). A *structured* scalar (e.g. `ProposalField.TeamLeader`, whose
 * value is a JSON object, not text) uses `scalarPath` and also carries `operation` — a `FieldSet` this
 * time, whose `value` the caller extracts via `proposalScalarFieldRevertValue` and re-applies through
 * its own setter, exactly like the collection-item case.
 */
export type ProposalFieldTimelineRevertEntry = {
  sequence: number;
  /** What the field read at that point, for a caller that shows it. */
  nextDisplayValue?: string;
  /** What the field HELD at that point — the value a scalar revert writes back. */
  nextValue?: string | number | boolean;
  operation?: ProposalContentOperation;
  actorUserId?: string | null;
  createdDate: string;
};

type ProposalFieldTimelineDropdownProps = {
  negotiationId?: string;
  viewerScope?: string;
  field: ProposalField;
  disabled?: boolean;
  toolTip?: string;
  /**
   * Overrides the collection item identity derived from `field.id`. Most collection items (fee
   * schedule categories, documents) are identified by a single id that IS `field.id.value`, but a
   * vendor-team member's canonical identity is the compound `userId:legalEntityId` (see
   * `proposal-operation-identity.ts` / `ProposalCollectionValuePolicy.cs`), which `field.id` alone
   * cannot express since `ProposalField.TeamMember(userId)` only carries the user id. Callers whose
   * item identity is compound must pass it explicitly here.
   */
  collectionItemId?: string;
  /**
   * The columns of this collection item that have histories of their own, in reading order.
   *
   * A fee-schedule category is negotiated one column at a time — the command builder emits an operation
   * per changed property, so a rate and a billing code are two events with two conflict keys — and
   * #4847 asks the history to work at that grain: browse and revert each column separately. Supplying
   * these turns the menu from one list of whole-item states into a section per column plus a
   * "Whole entry" section for the item arriving or leaving, and each column's revert queues only that column (see
   * `buildProposalCollectionItemAspectRevertOperation`), leaving every other column where it is.
   *
   * Omit it and the menu behaves exactly as before: whole-item states only. A collection whose items are
   * one indivisible thing — a document reference, a team member — has nothing to say here.
   */
  collectionItemAspects?: readonly ProposalCollectionItemAspect[];
  /**
   * Forces structured-scalar mode and overrides the semantic path derived from `field` for both the
   * timeline query and the revert candidates — for a scalar field whose `ProposalField` carries an id
   * for comment/history scoping (so it would otherwise be mistaken for a collection item) but whose
   * default field path is shared with sibling content it must not offer as a revert candidate.
   * `ProposalField.TeamLeader(id)` is the motivating case: its default path (`/vendorTeam`) is the whole
   * team thread shared with member add/update/remove, but a leader revert must only ever see
   * `/vendorTeam/leader` `FieldSet` entries. Takes priority over `field.id`/`collectionItemId` — a caller
   * passing this has already opted into scalar mode regardless of the field's id.
   *
   * In practice this is now redundant for `ProposalField.TeamLeader` specifically —
   * `field.timelineIdentity()` special-cases it unconditionally (see proposal-field.ts) — but the prop
   * stays as an explicit override for a future structured scalar `timelineIdentity` does not yet know
   * about. The closed `ProposalStructuredScalarPath` type means a typo'd path fails to compile rather
   * than silently querying a path with no history.
   */
  scalarPath?: ProposalStructuredScalarPath;
  /**
   * Called with the selected historical entry when the user reverts the field to it. See
   * {@link ProposalFieldTimelineRevertEntry} for what the caller receives and how to apply it.
   */
  onRevert: (entry: ProposalFieldTimelineRevertEntry) => void;
};

/**
 * Renders nothing until the negotiation stream exists (drafts before their first save, or the legacy
 * path). Keeping every Redux/RTK Query hook inside the inner component — mounted only once
 * negotiationId and viewerScope are present — lets the many call sites that render this without a
 * store (and without a negotiation) keep working untouched, mirroring `ProposalFieldHistoryButton`.
 */
export default function ProposalFieldTimelineDropdown(props: Readonly<ProposalFieldTimelineDropdownProps>) {
  const { negotiationId, viewerScope, field, disabled, collectionItemId, collectionItemAspects, scalarPath, onRevert } =
    props;
  const toolTip = props.toolTip ?? "Field change history";

  if (!negotiationId || !viewerScope) return null;

  return (
    <ConnectedProposalFieldTimelineDropdown
      negotiationId={negotiationId}
      viewerScope={viewerScope}
      field={field}
      disabled={disabled}
      toolTip={toolTip}
      collectionItemId={collectionItemId}
      collectionItemAspects={collectionItemAspects}
      scalarPath={scalarPath}
      onRevert={onRevert}
    />
  );
}

type ConnectedProposalFieldTimelineDropdownProps = Omit<
  ProposalFieldTimelineDropdownProps,
  "negotiationId" | "viewerScope" | "toolTip"
> & {
  negotiationId: string;
  viewerScope: string;
  toolTip: string;
};

function ConnectedProposalFieldTimelineDropdown({
  negotiationId,
  viewerScope,
  field,
  disabled,
  toolTip,
  collectionItemId,
  collectionItemAspects,
  scalarPath,
  onRevert,
}: Readonly<ConnectedProposalFieldTimelineDropdownProps>) {
  // The guard against a composite field control's ancestor click handler tearing the row down on the
  // same click that opened the menu, plus the page's standing request for this field's history.
  const { anchorEl, isOpen, open, close, triggerRef } =
    useProposalFieldHistoryPopoverTrigger<HTMLButtonElement>(field);
  const session = useSession();
  const currentUserId = session.user?.id?.value;
  // `scalarPath` forces structured-scalar mode regardless of `field.id` — see its doc comment above.
  // Otherwise the field answers for its own identity (see proposal-field.ts's `timelineIdentity`),
  // which is what makes forgetting `scalarPath` for a field like `ProposalField.TeamLeader` harmless:
  // there is no implicit "derive an itemId from field.id" fallback here anymore for a caller to omit.
  const identity: ProposalFieldTimelineIdentity =
    scalarPath !== undefined ? scalarWithIdTimelineIdentity(scalarPath) : field.timelineIdentity({ itemId: collectionItemId });
  const isStructuredScalar = identity.kind === "scalarWithId";
  const path = identity.kind === "scalarWithId" ? identity.path : proposalTimelinePathForField(field);
  const itemId = identity.kind === "collectionItem" ? identity.itemId : undefined;

  // The same badge state ProposalFieldHistoryButton uses: the unviewed-change count plus the
  // collision flag. Surfacing the collision matters because without it
  // the counterparty's (or a same-party reviewer's) change is only reachable by opening the revert
  // menu, where it is easy to miss.
  const badgeState = useProposalFieldChangeBadgeState(negotiationId, field, currentUserId);
  const { isCollision, unreadCount, latestUnseenSequence, hasPendingChange } = badgeState;
  const [markProposalChangesSeen] = useMarkProposalChangesSeenMutation();

  // Narrowed on the wire when this is one item of a collection (#5118), rather than reading the whole
  // collection's thread and keeping one item's entries out of it below.
  const queryArgs =
    isOpen && negotiationId && viewerScope ? { negotiationId, viewerScope, path, itemId } : skipToken;
  const timelineQuery = useGetProposalTimelineQuery(queryArgs);
  const rawEntries = useMemo(() => timelineQuery.data?.entries ?? [], [timelineQuery.data?.entries]);
  // A fee category's assigned member is a person, and a candidate to revert to reads as that person's
  // name (#4949) — from the store, which the lookup below fills with the members the entries name.
  const resolveUserName = useProposalUserNameResolver();

  // A scalar revert restores the value the field held at that point, so only scalar content changes
  // with a displayable value are offered.
  const scalarRevertableEntries = useMemo(() => {
    if (itemId) return [];
    const view = buildProposalTimelineView(rawEntries);
    return view
      .filter((entry) => entry.isContentChange && entry.nextDisplayValue !== undefined)
      .reverse()
      .slice(0, MAX_REVERT_ENTRIES);
  }, [itemId, rawEntries]);

  // A collection item's value is a structured object, so it never has a scalar display value — describe
  // it through the shared change vocabulary instead, and build the well-formed collection operation a
  // revert must queue (see proposal-collection-item-revert.ts) up front, so a caller can never
  // accidentally apply it as a raw FieldSet.
  const collectionRevertCandidates = useMemo(() => {
    if (!itemId) return [];

    const itemEntries = rawEntries
      .filter((entry) => entryBelongsToItem(entry, itemId))
      // With per-column sections above (see `aspectRevertGroups`), this section is about the item as a
      // whole — its arrivals and departures — so a single column's event is not one of its states. Its
      // payload states one property, and restoring THAT as the whole item would produce a category with
      // no name (#4847). Without columns supplied, every state of the item is a whole-item state.
      // The ENTRY's own path or nothing: `path` here is the field's comment-thread path
      // (`/feeSchedule`), never the collection's (`/feeSchedule/categories`), so substituting it hands
      // the key a path that cannot match — and a column event, unmatched, reads as a state of the whole
      // category. An empty path instead says "no path", which is what a pathless entry is, and the key
      // is then read by position (see `proposalCollectionItemConflictKeyAspect`).
      .filter(
        (entry) =>
          !collectionItemAspects?.length ||
          !proposalCollectionItemConflictKeyAspect(entry.path ?? "", itemId, entry.conflictKey),
      )
      .slice()
      .sort((left, right) => left.sequence - right.sequence);
    if (itemEntries.length === 0) return [];

    // The last entry for the item is its current known state, not a "previous" one — it is never
    // offered for revert, but it is what a revert's operation is expected against.
    const currentEntry = itemEntries[itemEntries.length - 1];
    const priorEntries = itemEntries.slice(0, -1).filter((entry) => entry.valueExists);

    return priorEntries
      .slice()
      .reverse()
      .slice(0, MAX_REVERT_ENTRIES)
      .flatMap((entry) => {
        // The entry's own path, or the one its conflict key was built from. Never the field's thread
        // path (`/feeSchedule`): a collection operation addressed there is not a weaker revert but a
        // wrong one, queued against a path the item does not live at. With neither available there is
        // no well-formed operation to offer, so nothing is offered.
        const itemPath = entry.path ?? proposalCollectionPathFromConflictKey(entry.conflictKey, itemId);
        if (!itemPath) return [];
        const operation = buildProposalCollectionItemRevertOperation({
          path: itemPath,
          itemId,
          currentEntry,
          targetEntry: entry,
        });
        if (!operation) return [];

        return [
          {
            key: `item-${entry.sequence}`,
            sequence: entry.sequence,
            createdDate: entry.createdDate,
            actorUserId: entry.actorUserId,
            actorRole: entry.actorRole,
            sourceParty: entry.sourceParty,
            // Named: a team member's or leader's candidate is a person, and "Added Team Member" with
            // nobody after it says which value to go back to no better than "(changed)" did (#4954).
            label: formatProposalChangeLine(describeProposalTimelineChange(entry, { resolveUserName })),
            isCreationBaseline: proposalTimelineEntryIsCreationBaseline(entry),
            operation,
          },
        ];
      });
  }, [collectionItemAspects, itemId, rawEntries, resolveUserName]);

  // One section per column, for a collection whose items are negotiated column by column (#4847).
  // Each column reads its own history out of the entries that state it — its own leaf events, plus the
  // whole-item events (an add, a removal, the creation baseline) that state every column at once — and
  // reverts through an operation touching only that column, so taking back a rate leaves the billing
  // code beside it exactly where the counterparty put it.
  const aspectRevertGroups = useMemo(() => {
    if (!itemId || !collectionItemAspects?.length) return [];

    const itemEntries = rawEntries
      .filter((entry) => entryBelongsToItem(entry, itemId) && proposalTimelineEntryIsContentChange(entry.kind))
      .slice()
      .sort((left, right) => left.sequence - right.sequence);

    // A column the caller did not declare still has a history, and the whole-item filter above has
    // already excluded its events from the "Whole entry" section -- correctly, since restoring one
    // column as the whole item would produce an item missing everything else. Without a section of its
    // own it is therefore reachable from neither, which is how a fee category's `teamMember` became
    // impossible to take back. So any column the entries actually carry gets one, named from its key.
    const declaredKeys = new Set(collectionItemAspects.map((aspect) => aspect.key));
    const discoveredAspects = [
      ...new Set(
        itemEntries.flatMap((entry) => {
          const column = proposalCollectionItemConflictKeyAspect(
            entry.path ?? proposalCollectionPathFromConflictKey(entry.conflictKey, itemId),
            itemId,
            entry.conflictKey,
          );
          return column && !declaredKeys.has(column) ? [column] : [];
        }),
      ),
    ].map((key) => ({ key, label: proposalCollectionItemAspectLabel(key) }));

    return [...collectionItemAspects, ...discoveredAspects].flatMap((aspect) => {
      const stated = itemEntries.flatMap((entry) => {
        const held = readProposalCollectionItemAspectValue(
          entry.conflictKey,
          entry.valueExists ? entry.value : undefined,
          aspect.key,
        );
        return held.exists ? [{ entry, value: held.value }] : [];
      });
      // The last statement is what the column holds now, not a previous value; without one behind it
      // there is nothing to go back to.
      if (stated.length < 2) return [];
      const current = stated[stated.length - 1];

      const candidates = stated
        .slice(0, -1)
        .reverse()
        .flatMap((target) => {
          // A value identical to the current one is not a revert, it is a no-op wearing a timestamp.
          if (proposalStrictJsonValuesEqual(target.value, current.value)) return [];
          // The ENTRY's path, or the one its conflict key was built from — never the field's, which is
          // the collection's parent thread (`/feeSchedule`) rather than the path the column lives at
          // (`/feeSchedule/categories`). Substituting it left the target reading `[Empty]`, because the
          // shared vocabulary unwraps a column only at the collection path, and built the revert against
          // `/feeSchedule`. A target whose path cannot be established is not offered at all.
          const valuePath = target.entry.path ?? proposalCollectionPathFromConflictKey(target.entry.conflictKey, itemId);
          if (!valuePath) return [];
          // What the column held, in the shared vocabulary. A value the vocabulary cannot read is not
          // an empty one: "[Empty]" would claim the column was cleared, when a reassigned team member
          // is a value this surface simply cannot show.
          const valueText =
            proposalChangeValueText(valuePath, { [aspect.key]: target.value }, { resolveUserName }) ??
            (target.value === undefined || target.value === null
              ? proposalEmptyValueText
              : proposalCollectionItemUnreadableValueText);
          return [
            {
              key: `aspect-${aspect.key}-${target.entry.sequence}`,
              sequence: target.entry.sequence,
              createdDate: target.entry.createdDate,
              actorUserId: target.entry.actorUserId,
              actorRole: target.entry.actorRole,
              sourceParty: target.entry.sourceParty,
              label: valueText,
              isCreationBaseline: proposalTimelineEntryIsCreationBaseline(target.entry),
              operation: buildProposalCollectionItemAspectRevertOperation({
                path: valuePath,
                itemId,
                aspect: aspect.key,
                currentValue: current.value,
                targetValue: target.value,
              }),
            },
          ];
        })
        .slice(0, MAX_REVERT_ENTRIES);

      return candidates.length ? [{ label: aspect.label, candidates }] : [];
    });
  }, [collectionItemAspects, itemId, rawEntries, resolveUserName]);

  // A structured scalar (e.g. team leader) has no itemId to group by — the query is already scoped
  // exactly to its own path via `scalarPath` — but still needs a built FieldSet operation the same way
  // a collection item needs a built CollectionItemUpdated/Added: there is no display string for the
  // caller's setter to take directly (see buildProposalScalarFieldRevertOperation).
  const structuredScalarRevertCandidates = useMemo(() => {
    if (!isStructuredScalar) return [];

    const orderedEntries = rawEntries
      .filter((entry) => proposalTimelineEntryIsContentChange(entry.kind))
      .slice()
      .sort((left, right) => left.sequence - right.sequence);
    if (orderedEntries.length === 0) return [];

    // The last entry is the field's current known state, not a "previous" one — never offered for
    // revert, but what a revert's operation is expected against.
    const currentEntry = orderedEntries[orderedEntries.length - 1];
    const priorEntries = orderedEntries.slice(0, -1).filter((entry) => entry.valueExists);

    return priorEntries
      .slice()
      .reverse()
      .slice(0, MAX_REVERT_ENTRIES)
      .flatMap((entry) => {
        const operation = buildProposalScalarFieldRevertOperation({
          path: entry.path ?? path ?? "",
          currentEntry,
          targetEntry: entry,
        });
        if (!operation) return [];

        return [
          {
            key: `scalar-${entry.sequence}`,
            sequence: entry.sequence,
            createdDate: entry.createdDate,
            actorUserId: entry.actorUserId,
            sourceParty: entry.sourceParty,
            actorRole: entry.actorRole,
            // Named: a team member's or leader's candidate is a person, and "Added Team Member" with
            // nobody after it says which value to go back to no better than "(changed)" did (#4954).
            label: formatProposalChangeLine(describeProposalTimelineChange(entry, { resolveUserName })),
            isCreationBaseline: proposalTimelineEntryIsCreationBaseline(entry),
            operation,
          },
        ];
      });
  }, [isStructuredScalar, rawEntries, path, resolveUserName]);

  const authorUserIds = useMemo(() => {
    const scalarAuthors = scalarRevertableEntries.flatMap((entry) =>
      entry.actorRole !== "System" && entry.actorUserId ? [entry.actorUserId] : [],
    );
    const collectionAuthors = collectionRevertCandidates.flatMap((candidate) =>
      candidate.actorRole !== "System" && candidate.actorUserId ? [candidate.actorUserId] : [],
    );
    const structuredScalarAuthors = structuredScalarRevertCandidates.flatMap((candidate) =>
      candidate.actorRole !== "System" && candidate.actorUserId ? [candidate.actorUserId] : [],
    );
    const aspectAuthors = aspectRevertGroups.flatMap((group) =>
      group.candidates.flatMap((candidate) =>
        candidate.actorRole !== "System" && candidate.actorUserId ? [candidate.actorUserId] : [],
      ),
    );
    // The people a fee category's team-member column names, on either side of each change: the
    // candidates read as their names, so the names have to be fetched with the authors'.
    const feeScheduleMembers = rawEntries.flatMap(proposalFeeScheduleTeamMemberUserIds);
    // And the people a team change is about, on either side: the candidates read as their names.
    const teamMembers = rawEntries.flatMap((entry) => {
      const change = proposalTimelineTeamChange(entry);
      return [change?.previousUserId, change?.nextUserId].filter((userId): userId is string => Boolean(userId));
    });
    return new Set([
      ...scalarAuthors,
      ...collectionAuthors,
      ...structuredScalarAuthors,
      ...aspectAuthors,
      ...feeScheduleMembers,
      ...teamMembers,
    ]);
  }, [aspectRevertGroups, collectionRevertCandidates, rawEntries, scalarRevertableEntries, structuredScalarRevertCandidates]);
  useGetIndividualsByUserIdsQuery({ userIds: authorUserIds }, { skip: authorUserIds.size === 0 });

  const hasRevertCandidates = itemId
    ? collectionRevertCandidates.length > 0 || aspectRevertGroups.length > 0
    : isStructuredScalar
      ? structuredScalarRevertCandidates.length > 0
      : scalarRevertableEntries.length > 0;

  // Opening the history is the acknowledgement, exactly as it is for ProposalFieldHistoryButton and for
  // a comment thread: advance this field's seen cursor past what was just surfaced so the count clears.
  function handleOpen(event: MouseEvent<HTMLButtonElement>) {
    open(event);
    // A fee-schedule category acknowledges itself and not its siblings (#5118); see the same line in
    // ProposalFieldHistoryButton.
    const seenPath = proposalTimelineItemPathForField(field) ?? proposalTimelinePathForField(field);
    if (seenPath && unreadCount > 0) {
      void markProposalChangesSeen({
        negotiationId,
        path: seenPath,
        throughStreamVersion: latestUnseenSequence,
      });
    }
  }

  // Escape closes it. A `Menu` got this from `Modal`, which also took focus; this list
  // deliberately leaves focus where the reader had it, so the key never reaches the list itself and
  // the document is the only place that hears it.
  //
  // `close` is read through a ref because the page rebuilds the history request it closes over on every
  // render: naming it as a dependency would tear the listener down and rebuild it just as often, while
  // `isOpen` is what actually decides whether anything should be listening.
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });
  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") closeRef.current();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  function handleScalarRevert(entry: ProposalTimelineEntryView) {
    close();
    onRevert({
      sequence: entry.sequence,
      nextDisplayValue: entry.nextDisplayValue,
      nextValue: entry.nextValue,
      actorUserId: entry.actorUserId,
      createdDate: entry.createdDate,
    });
  }

  function handleCollectionRevert(candidate: {
    sequence: number;
    createdDate: string;
    actorUserId?: string | null;
    operation: ProposalContentOperation;
  }) {
    close();
    onRevert({
      sequence: candidate.sequence,
      operation: candidate.operation,
      actorUserId: candidate.actorUserId,
      createdDate: candidate.createdDate,
    });
  }

  return (
    <>
      <Tooltip title={isCollision ? "A change here needs your attention" : toolTip}>
        <span>
          <IconButton
            ref={triggerRef}
            aria-label={
              badgeState.badgeLabel ? `${toolTip} (${badgeState.badgeLabel})` : toolTip
            }
            aria-haspopup="menu"
            aria-expanded={isOpen}
            disabled={disabled}
            size="medium"
            onClick={handleOpen}
          >
            <HistoryIconContainer>
              <ProposalFieldChangeBadgeBubbles state={badgeState} />
              <HistoryIcon
                color={isCollision ? "error" : hasPendingChange ? "info" : "inherit"}
                fontSize="medium"
              />
            </HistoryIconContainer>
          </IconButton>
        </span>
      </Tooltip>
      <Popper
        anchorEl={anchorEl}
        open={isOpen}
        placement="bottom-start"
        // `Popper` names its own root `role="tooltip"`, which would wrap this menu in a tooltip.
        role="presentation"
        // Above the page's own drawers and sticky headers; a `Menu` used to get this for free.
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={close}>
          <Paper elevation={8}>
            <MenuList aria-label="Field change history" dense>
              <ListSubheader component="div" disableSticky>
                Revert to a previous value
              </ListSubheader>
              {timelineQuery.isLoading || timelineQuery.isFetching ? (
                <MenuItem disabled>Loading…</MenuItem>
              ) : !hasRevertCandidates ? (
                <MenuItem disabled>No previous values</MenuItem>
              ) : itemId ? (
                [
                  // A column at a time first — that is the grain the schedule is negotiated and read in — then
                  // the item as a whole, which is where its arrival and departure live.
                  ...aspectRevertGroups.flatMap((group) => [
                    <ListSubheader key={`aspect-${group.label}`} component="div" disableSticky>
                      {group.label}
                    </ListSubheader>,
                    ...group.candidates.map((candidate) => (
                      <CollectionItemRevertMenuItem
                        key={candidate.key}
                        candidate={candidate}
                        onRevert={handleCollectionRevert}
                      />
                    )),
                  ]),
                  ...(collectionRevertCandidates.length > 0
                    ? [
                      aspectRevertGroups.length > 0 ? (
                        <ListSubheader key="whole-item" component="div" disableSticky>
                          {wholeItemSectionLabel}
                        </ListSubheader>
                      ) : null,
                      ...collectionRevertCandidates.map((candidate) => (
                        <CollectionItemRevertMenuItem
                          key={candidate.key}
                          candidate={candidate}
                          onRevert={handleCollectionRevert}
                        />
                      )),
                    ]
                    : []),
                ]
              ) : isStructuredScalar ? (
                structuredScalarRevertCandidates.map((candidate) => (
                  <CollectionItemRevertMenuItem
                    key={candidate.key}
                    candidate={candidate}
                    onRevert={handleCollectionRevert}
                  />
                ))
              ) : (
                scalarRevertableEntries.map((entry) => (
                  <RevertMenuItem key={entry.sequence} entry={entry} onRevert={handleScalarRevert} />
                ))
              )}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

function RevertMenuItem({
  entry,
  onRevert,
}: Readonly<{ entry: ProposalTimelineEntryView; onRevert: (entry: ProposalTimelineEntryView) => void }>) {
  const authorGuid = useMemo(() => {
    if (entry.actorRole === "System" || !entry.actorUserId) return undefined;
    try {
      return new Guid(entry.actorUserId);
    } catch {
      return undefined;
    }
  }, [entry.actorRole, entry.actorUserId]);
  const individual = useSelector(getIndividualByUserId(authorGuid));
  const author = proposalChangeActorLabel({
    actorRole: entry.actorRole,
    resolvedName: individual?.fullName,
    sourceParty: entry.sourceParty,
  });

  return (
    <MenuItem onClick={() => onRevert(entry)}>
      <ListItemIcon>
        <RestoreIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText
        primary={entry.nextDisplayValue}
        secondary={
          <Typography component="span" variant="caption" color="text.secondary">
            {entry.isCreationBaseline
              ? proposalCreationBaselineLabel
              : `${author} · ${new Date(entry.createdDate).toLocaleString()}`}
          </Typography>
        }
      />
    </MenuItem>
  );
}

type CollectionRevertCandidate = {
  key: string;
  sequence: number;
  createdDate: string;
  actorUserId?: string | null;
  sourceParty?: ProposalNegotiationParty | null;
  actorRole?: ProposalActorRole | null;
  label: string;
  isCreationBaseline: boolean;
  operation: ProposalContentOperation;
};

function CollectionItemRevertMenuItem({
  candidate,
  onRevert,
}: Readonly<{
  candidate: CollectionRevertCandidate;
  onRevert: (candidate: CollectionRevertCandidate) => void;
}>) {
  const authorGuid = useMemo(() => {
    if (candidate.actorRole === "System" || !candidate.actorUserId) return undefined;
    try {
      return new Guid(candidate.actorUserId);
    } catch {
      return undefined;
    }
  }, [candidate.actorRole, candidate.actorUserId]);
  const individual = useSelector(getIndividualByUserId(authorGuid));
  const author = proposalChangeActorLabel({
    actorRole: candidate.actorRole,
    resolvedName: individual?.fullName,
    sourceParty: candidate.sourceParty,
  });

  return (
    <MenuItem onClick={() => onRevert(candidate)}>
      <ListItemIcon>
        <RestoreIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText
        primary={candidate.label}
        secondary={
          <Typography component="span" variant="caption" color="text.secondary">
            {candidate.isCreationBaseline
              ? proposalCreationBaselineLabel
              : `${author} · ${new Date(candidate.createdDate).toLocaleString()}`}
          </Typography>
        }
      />
    </MenuItem>
  );
}
